// The jump bar's sources (see jump/complete.ts): what the text names, the
// current repository's branches and open pull requests, the pull requests
// waiting on the viewer, and recents and pins. Each is given what it reads
// (jump/github.ts gives the app's), so none reaches for the network or
// storage itself.
//
// Where each goes in the list is its id's place in jump/github.ts
// `SOURCE_ORDER`; the repository index's repositories (`repositoriesSource`)
// plug in there by their id (`repositories`, App.svelte).
import { parseSubject } from '../api/relaySubject';
import { isRefName } from '../subject';
import { subjectPath } from '../router';
import type { DashSnapshot } from '../state/dashCache';
import { sameLogin } from '../logic/attention';
import { PartialAnswer, type JumpQuery, type Source } from './complete';
import { interpret, type InterpretContext } from './interpret';
import { branchRow, ranked, repoRow, subjectRow, type BranchPull, type Place, type Repo, type Row } from './rows';

const repoKey = (r: Repo) => `${r.owner}/${r.repo}`.toLowerCase();

/** What was read, by key, while the bar is open; a read that failed is not kept, and is tried again. */
function keeper<T>(): { get(key: string, read: () => Promise<T>): Promise<T>; clear(): void } {
  const kept = new Map<string, Promise<T>>();
  return {
    get(key, read) {
      let got = kept.get(key);
      if (!got) {
        got = read();
        kept.set(key, got);
        got.catch(() => kept.delete(key));
      }
      return got;
    },
    clear: () => kept.clear(),
  };
}

/** What the text names by itself (jump/interpret.ts). */
export function directSource(ctx: () => Omit<InterpretContext, 'repo'>): Source {
  return { id: 'direct', title: 'Go to', rows: (q) => interpret(q.text, { ...ctx(), repo: q.repo }) };
}

/** A repository's branches whose names match a prefix (GitHub's best first), and its default branch. */
export interface Branches {
  defaultBranch: string | null;
  branches: readonly { name: string; pull: BranchPull | null }[];
}

/** How long the typing rests before the branches are asked for. */
export const BRANCH_DEBOUNCE_MS = 150;

/** What can start a branch name (`isRefName`'s rules for a prefix): no space, `~^:?*[\`, `..`, `//`, and no `#` to start. */
const branchPrefix = (t: string) => !!t && !/[\s~^:?*[\\]/.test(t) && !t.includes('..') && !t.includes('//') && !t.startsWith('#');

/** Where a text's branches are read: the repository, the prefix of a name in it, and how the text writes one of its branches (a fork's as `ana:feat`). */
export interface BranchScope {
  repo: Repo;
  prefix: string;
  written: (name: string) => string;
}

export interface BranchSourceOptions {
  debounceMs?: number;
  /** Where the text's branches are read (the query's repository, the text its prefix, by default); null: nowhere. */
  scope?: (q: JumpQuery) => BranchScope | null;
  /** A branch's row (the jump bar's `branchRow` by default); null leaves it out. */
  row?: (at: BranchScope, branch: Branches['branches'][number], defaultBranch: string | null) => Row | null;
  /** An empty prefix is asked too, for the latest committed to (a header's picker, opened before anything is typed). */
  empty?: boolean;
}

const hereScope = (q: JumpQuery): BranchScope | null => (q.repo ? { repo: q.repo, prefix: q.text.trim(), written: (name) => name } : null);

/**
 * The current repository's branches starting with what is typed (then
 * those with a word that does), each with its open pull request: `read`
 * asks GitHub for the prefix, once the typing rests. What was read is
 * kept per text while the bar is open (a read that failed is not). A
 * header's picker (jump/pick.ts) gives its own scope (a fork's branches
 * for `ana:`) and rows (each one choice).
 */
export function branchSource(read: (repo: Repo, prefix: string, signal: AbortSignal) => Promise<Branches | null>, o: BranchSourceOptions = {}): Source {
  const kept = keeper<Branches | null>();
  const scope = o.scope ?? hereScope;
  const row = o.row ?? ((at, x, defaultBranch) => branchRow(at.repo, x.name, { defaultBranch, pull: x.pull }));
  return {
    id: 'branches',
    title: 'Branches',
    debounceMs: o.debounceMs ?? BRANCH_DEBOUNCE_MS,
    wants: (q) => {
      const at = scope(q);
      return !!at && (at.prefix ? branchPrefix(at.prefix) : !!o.empty);
    },
    reset: () => kept.clear(),
    async rows(q: JumpQuery): Promise<Row[]> {
      const at = scope(q)!;
      const b = await kept.get(`${repoKey(at.repo)}\n${at.prefix}`, () => read(at.repo, at.prefix, q.signal));
      if (!b) return [];
      return ranked(at.prefix, b.branches, (x) => [x.name]).flatMap((x) => row(at, x, b.defaultBranch) ?? []);
    },
  };
}

/** One of a repository's open pull requests. */
export interface OpenPull {
  number: number;
  title: string;
  author: string | null;
}

/** The current repository's open pull requests matching what is typed: by number, title or author. */
export function pullSource(read: (repo: Repo) => Promise<readonly OpenPull[] | null>): Source {
  const kept = keeper<readonly OpenPull[] | null>();
  return {
    id: 'pulls',
    title: 'Open pull requests',
    wants: (q) => !!q.repo && !!q.text.trim(),
    reset: () => kept.clear(),
    async rows(q) {
      const repo = q.repo!;
      const pulls = (await kept.get(repoKey(repo), () => read(repo))) ?? [];
      return ranked(q.text.replace(/^#/, ''), pulls, (p) => [String(p.number), p.title, p.author]).map((p) => subjectRow({ kind: 'pull', owner: repo.owner, repo: repo.repo, number: p.number }, { title: p.title }));
    },
  };
}

/** A pull request whose turn is the viewer's. */
export interface WaitingPull {
  owner: string;
  repo: string;
  number: number;
  title: string;
  author: string | null;
}

/**
 * The pull requests of the dashboard's saved rows (state/dashCache.ts,
 * what the dashboard last read) whose attention set holds `me`: each once,
 * in the dashboard's order.
 */
export function waitingOf(snapshot: DashSnapshot | null, me: string): WaitingPull[] {
  if (!snapshot || !me) return [];
  const out: WaitingPull[] = [];
  const seen = new Set<string>();
  for (const pr of snapshot.sections.flatMap((s) => s.prs)) {
    if (seen.has(pr.id) || !snapshot.attention[pr.id]?.some((m) => sameLogin(m.login, me))) continue;
    seen.add(pr.id);
    out.push({ owner: pr.repository.owner.login, repo: pr.repository.name, number: pr.number, title: pr.title, author: pr.author?.login ?? null });
  }
  return out;
}

/**
 * Every pull request of the dashboard's saved rows (each once, in the
 * dashboard's order), the stack members it read by ID too: what the bar
 * completes typed text against, whoever's turn it is.
 */
export function dashboardOf(snapshot: DashSnapshot | null): WaitingPull[] {
  if (!snapshot) return [];
  const out: WaitingPull[] = [];
  const seen = new Set<string>();
  for (const pr of [...snapshot.sections.flatMap((s) => s.prs), ...snapshot.outside.prs]) {
    if (seen.has(pr.id)) continue;
    seen.add(pr.id);
    out.push({ owner: pr.repository.owner.login, repo: pr.repository.name, number: pr.number, title: pr.title, author: pr.author?.login ?? null });
  }
  return out;
}

/** Dashboard pull requests matching `text`, as rows. */
function pullRows(text: string, pulls: readonly WaitingPull[]): Row[] {
  return ranked(text.replace(/^#/, ''), pulls, (p) => [String(p.number), p.title, `${p.owner}/${p.repo}`, p.author]).map((p) => subjectRow({ kind: 'pull', owner: p.owner, repo: p.repo, number: p.number }, { title: p.title }));
}

/** The pull requests waiting on the viewer, anywhere: read once each time the bar opens. */
export function waitingSource(read: () => Promise<readonly WaitingPull[]>): Source {
  const kept = keeper<readonly WaitingPull[]>();
  return { id: 'waiting', title: 'Waiting on you', reset: () => kept.clear(), rows: async (q) => pullRows(q.text, await kept.get('', read)) };
}

/** Every pull request on the dashboard, anywhere, for a typed text (those waiting on the viewer are listed above it). */
export function dashboardSource(read: () => Promise<readonly WaitingPull[]>): Source {
  const kept = keeper<readonly WaitingPull[]>();
  return { id: 'dashboard', title: 'On your dashboard', wants: (q) => !!q.text.trim(), reset: () => kept.clear(), rows: async (q) => pullRows(q.text, await kept.get('', read)) };
}

/** What the repositories source reads: the repository index (state/repos.svelte.ts `RepoIndex`), its listing read once a page. */
export interface RepoListing {
  readonly status: 'idle' | 'loading' | 'ready' | 'failed';
  /** Read the listing, once a page (joining a read under way; after a failure, afresh). */
  load(): Promise<void>;
  /** Resolves once the listing is read (every account listed) or failed, without waiting for what orders it. */
  listed(): Promise<void>;
  /** The accounts whose repositories could not be listed, while the others are (`RepoIndex.unlisted`). */
  readonly unlisted: readonly { login: string }[];
  /** Its rows whose `owner/name` holds each word of the text, best first, at most `limit`: while the listing fails, those only a pin, the dashboard or a push names. */
  filter(q: { text?: string }, limit: number): readonly { owner: string; name: string; group: 'pinned' | 'waiting' | 'pushed' | 'rest'; waiting: number }[];
}

/** Words a repository's `owner/name` could hold: GitHub's name characters, at most one `/` a word. */
const REPO_WORDS = /^\s*[\w.-]+(?:\/[\w.-]*)?(?:\s+[\w.-]+(?:\/[\w.-]*)?)*\s*$/;

const REPO_DETAIL = { pinned: 'pinned', pushed: 'you pushed recently' } as const;

/** How long a failed listing is not read again while the bar stays open (the coverage check's minute). */
export const LISTING_RETRY_MS = 60_000;

/**
 * The repository index's repositories matching what is typed, in its own
 * order (pinned, waiting on the viewer, pushed to recently, the rest),
 * best matches first. The index is made and its listing read only for a
 * text a repository's name could hold, so a page that never opens the
 * bar, or opens it only to choose a pull request, reads none of it; the
 * listing is then the page's, read once, and answered from as soon as it
 * is read (every account listed), not once all that orders it is.
 *
 * A listing that failed is read again once: when the bar opens again, or
 * LISTING_RETRY_MS on; never on each keystroke. Meanwhile what the index
 * lists without it (a pin that matches) is listed, and the source is
 * named as failed (`PartialAnswer`), so the bar says the rest could not
 * be read. Every ask waits for a read under way, whichever ask started
 * it. An account whose repositories could not be listed while the others
 * were (`unlisted`) is named the same way: "Repositories of <account>".
 */
export function repositoriesSource(index: () => RepoListing, o: { limit?: number; now?: () => number } = {}): Source {
  const limit = o.limit ?? 8;
  const now = o.now ?? Date.now;
  /** When the listing last failed to be read; null while it has not. */
  let failedAt: number | null = null;
  /** The bar opened again since the listing failed: it is read once more. */
  let opened = false;
  return {
    id: 'repositories',
    title: 'Repositories',
    limit,
    wants: (q) => REPO_WORDS.test(q.text) && !q.text.includes('..'),
    reset: () => {
      if (failedAt !== null) opened = true;
    },
    async rows(q) {
      const ix = index();
      const read = failedAt === null || opened || now() - failedAt >= LISTING_RETRY_MS;
      if (read) {
        opened = false;
        // Its failure is the status's to say; what orders it is not waited for.
        ix.load().catch(() => {});
      }
      // Every ask waits for the read under way, this one's or an earlier ask's (at once when none is): none answers from a listing still loading.
      await ix.listed();
      if (read) failedAt = ix.status === 'failed' ? now() : null;
      const found = ix
        .filter({ text: q.text }, limit)
        .map((r) => {
          const detail = r.group === 'waiting' ? `${r.waiting} waiting on you` : r.group === 'rest' ? undefined : REPO_DETAIL[r.group];
          return repoRow({ owner: r.owner, repo: r.name }, detail ? { detail } : {});
        });
      if (ix.status === 'failed') {
        if (found.length) throw new PartialAnswer(found, { message: 'Some repositories could not be listed' });
        throw new Error('The repositories could not be listed');
      }
      // Accounts whose listing failed while the others were listed: their repositories are not absent, only not read.
      if (ix.unlisted.length) throw new PartialAnswer(found, { what: `Repositories of ${ix.unlisted.map((u) => u.login).join(', ')}` });
      return found;
    },
  };
}

/** A repository's name, as GitHub spells it, from `owner/repo` as typed. */
export const REPOSITORY_DEBOUNCE_MS = 150;

/**
 * The repository a text `owner/repo` names, when GitHub shows it: the row
 * that takes the place of the guess the text made, so `octo/hello` typed
 * in a repository opens that repository rather than a branch of that name.
 */
export function repositorySource(read: (r: Repo, signal: AbortSignal) => Promise<Repo | null>, o: { debounceMs?: number } = {}): Source {
  const kept = keeper<Repo | null>();
  const named = (t: string) => /^([^/\s#@:]+)\/([^/\s#@:]+)$/.exec(t.trim());
  return {
    id: 'repository',
    title: 'Repository',
    debounceMs: o.debounceMs ?? REPOSITORY_DEBOUNCE_MS,
    wants: (q) => !!named(q.text) && !q.text.includes('..'),
    reset: () => kept.clear(),
    async rows(q) {
      const [, owner, repo] = named(q.text)!;
      const found = await kept.get(`${owner}/${repo}`.toLowerCase(), () => read({ owner, repo }, q.signal));
      return found ? [repoRow(found)] : [];
    },
  };
}

/** A commit's files (and, from a partial listing, the folders read), as listed. */
export interface RefTree {
  sha: string;
  paths: readonly string[];
  /** GitHub listed only part of it (a very large repository): only the folders on the way to the prefix were read. */
  truncated: boolean;
}

export const PATH_DEBOUNCE_MS = 150;

/** How a paths source reads: the commit a ref names, then its paths (for a partial listing, those on the way to `prefix`). */
export interface PathReads {
  commit(r: Repo, ref: string, signal: AbortSignal): Promise<string | null>;
  list(r: Repo, commit: string, prefix: string): Promise<RefTree>;
}

/**
 * After `ref//`, the paths in the ref starting with what follows, the
 * nearest first: each a history filtered to it. The ref's commit is found
 * once while the bar is open; the listing is the commit's (kept by it).
 * A listing GitHub cut short says so in a row of its own, last.
 */
export function pathSource(read: PathReads, o: { debounceMs?: number } = {}): Source {
  const kept = keeper<string | null>();
  const split = (t: string) => {
    const cut = t.indexOf('//');
    return cut < 0 ? null : { ref: t.slice(0, cut), prefix: t.slice(cut + 2) };
  };
  return {
    id: 'paths',
    title: 'Paths',
    limit: PATHS_LISTED,
    debounceMs: o.debounceMs ?? PATH_DEBOUNCE_MS,
    wants: (q) => {
      const at = split(q.text.trim());
      return !!q.repo && !!at && isRefName(at.ref);
    },
    reset: () => kept.clear(),
    async rows(q) {
      const repo = q.repo!;
      const { ref, prefix } = split(q.text.trim())!;
      const commit = await kept.get(`${repo.owner}/${repo.repo}\n${ref}`.toLowerCase(), () => read.commit(repo, ref, q.signal));
      if (!commit) return [];
      const tree = await read.list(repo, commit, prefix);
      // Every folder of the listing too, whether GitHub listed it or only its files.
      const all = new Set<string>();
      for (const p of tree.paths) {
        all.add(p);
        for (let i = p.indexOf('/'); i > 0; i = p.indexOf('/', i + 1)) all.add(p.slice(0, i));
      }
      const depth = (p: string) => p.split('/').length;
      const rows = [...all]
        .filter((p) => p.startsWith(prefix) && p !== prefix.replace(/\/$/, ''))
        .sort((a, b) => depth(a) - depth(b) || (a < b ? -1 : a > b ? 1 : 0))
        // Room kept for the partial listing's row, so the merge's limit never cuts it.
        .slice(0, tree.truncated ? PATHS_LISTED - 1 : PATHS_LISTED)
        .flatMap((path) => {
          const s = parseSubject(`${repo.owner}/${repo.repo}:${ref}//${path}`);
          return s ? [subjectRow(s)] : [];
        });
      if (!tree.truncated) return rows;
      // The folder what is typed is in: its history is the way on.
      const dir = prefix.includes('/') ? prefix.slice(0, prefix.lastIndexOf('/')) : '';
      const at = parseSubject(`${repo.owner}/${repo.repo}:${ref}${dir ? '//' + dir : ''}`);
      if (!at) return rows;
      const partial: Row = {
        id: `paths-partial:${repo.owner}/${repo.repo}:${ref}`.toLowerCase(),
        title: 'Some paths are not listed',
        detail: `GitHub listed this tree in part: only the folders on the way to ${prefix || 'its root'} were read`,
        actions: [{ kind: 'go', label: 'History', href: subjectPath(at) }],
      };
      return [...rows, partial];
    },
  };
}

/** How many paths a prefix lists, at most: the source's limit. */
const PATHS_LISTED = 8;

/** Kept places as rows, matching what is typed by key, title or detail: a subject's, or a repository's. */
function placeRows(text: string, list: readonly Place[]): Row[] {
  return ranked(text, list, (p) => [p.key.replace(/^repo:/, ''), p.title, p.detail]).flatMap((p) => {
    const repo = /^repo:([^/]+)\/(.+)$/.exec(p.key);
    if (repo) {
      // GitHub's spelling, kept as its title when pinned.
      const spelled = p.title && /^([^/]+)\/([^/]+)$/.exec(p.title);
      return [repoRow(spelled ? { owner: spelled[1], repo: spelled[2] } : { owner: repo[1], repo: repo[2] }, p.detail ? { detail: p.detail } : {})];
    }
    const s = parseSubject(p.key);
    return s ? [subjectRow(s, { ...(p.title ? { title: p.title } : {}), ...(p.detail ? { detail: p.detail } : {}) })] : [];
  });
}

/** The subjects visited, the latest first. */
export function recentSource(recents: () => readonly Place[]): Source {
  return { id: 'recents', title: 'Recent', rows: (q) => placeRows(q.text, recents()) };
}

/** The subjects pinned. */
export function pinSource(pins: () => readonly Place[]): Source {
  return { id: 'pins', title: 'Pinned', limit: 20, rows: (q) => placeRows(q.text, pins()) };
}
